import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import axiosConstants from "./axiosConstants";

const settings = {
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Headers": "*",
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Credentials": true,
    "X-XSRF-TOKEN": axiosConstants.XSRF,
    Accept: "application/json"
  }
};

interface AxiosInstanceType {
  [x: string]: any;
  get: (url: string, request?: AxiosRequestConfig) => Promise<AxiosResponse>;
  post: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  put: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  patch: (
    url: string,
    data?: any,
    request?: AxiosRequestConfig
  ) => Promise<AxiosResponse>;
  delete: (url: string, request?: AxiosRequestConfig) => Promise<AxiosResponse>;
}

const getAxiosInstance = (
  _instance: AxiosInstance,
  getAdditionalHeaders?: () => Record<string, string>
) => {
  return {
    async get(url: string, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.get(url, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async post(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.post(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async put(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.put(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async patch(url: string, data: any, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.patch(url, data, {
          ...(config || {}),
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async delete(url: string, config?: AxiosRequestConfig) {
      try {
        const response = await _instance.delete(url, {
          ...config,
          headers: {
            ...(getAdditionalHeaders?.() || {}),
            ...(config?.headers || {})
          }
        });
        return await Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    }
  };
};

const baseURLBatchCredit =
  process.env.NODE_ENV === "production"
    ? "batch-credit-ui-portlet"
    : "https://batch-credit-processor.k8.isw.la/batch-credit-processor";

const instanceBatchCredit = axios.create({
  ...settings,
  baseURL: baseURLBatchCredit
});

if (process.env.NODE_ENV !== "production") {
  instanceBatchCredit.defaults.headers.common.Authorization =
    "Bearer eyJhbGciOiJSUzI1NiJ9.eyJsYXN0TmFtZSI6ImlzdyIsIm1lcmNoYW50X2NvZGUiOiJNWDE1Nzg1IiwiZmlyc3RMb2dpbiI6ZmFsc2UsInVzZXJfbmFtZSI6ImFnYmlzd0BzaGFya2xhc2Vycy5jb20iLCJyZXF1ZXN0b3JfaWQiOiIxMjM5ODQ4NDc3MyIsImRvbWFpbl9jb2RlIjoiSVNXIiwibW9iaWxlTm8iOm51bGwsImNsaWVudF90eXBlIjoiREVGQVVMVCIsImFwcGxpY2F0aW9uX2lkIjoiNSIsInBheWFibGVfaWQiOiIzNjA0NCIsImNsaWVudF9pZCI6Imlzdy1wb3J0YWwiLCJpbnN0aXR1dGlvbl9pZCI6IjIwIiwiZmlyc3ROYW1lIjoiYWdlbmN5X2JhbmtpbmciLCJlbWFpbFZlcmlmaWVkIjp0cnVlLCJhdWQiOlsiYXJiaXRlciIsImNhZXNhciIsImNhcmRsZXNzLXNlcnZpY2UiLCJoaW1zLXBvcnRsZXQiLCJpbmNvZ25pdG8iLCJpc3ctY29sbGVjdGlvbnMiLCJpc3ctY29yZSIsImlzdy1sZW5kaW5nLXNlcnZpY2UiLCJpc3ctcGF5bWVudGdhdGV3YXkiLCJpc3ctcG9ydGFsIiwiaXN3LXBvc3Qtb2ZmaWNlIiwibmlic3MtaWRyaXMtY29weS1zZXR0aW5ncyIsInBhc3Nwb3J0IiwicG9zdGNhcmQtYXBpIiwicHJvamVjdC14LW1lcmNoYW50IiwicmVjdXJyZW50LWJpbGxpbmctYXBpIiwicmVmZXJyYWwtc2VydmljZS1hcGkiLCJzYWZldG9rZW4tYWRtaW4tYXBpIiwidHJhbnNmZXItc2VydmljZS1hZG1pbiIsInRyYW5zZmVyLXNlcnZpY2UtY29yZSIsInRyYW5zZmVyLXNlcnZpY2UtcG9ydGxldCIsInRzLW5hbWUtZW5xdWlyeSIsInZhdWx0Iiwid2FsbGV0Iiwid2VicGF5LXBvcnRsZXQiXSwiY29yZV9pZCI6IjEwMCIsInNjb3BlIjpbInByb2ZpbGUiXSwiZXhwIjoxNzA3NTEzNTQzLCJtb2JpbGVOb1ZlcmlmaWVkIjpmYWxzZSwiY2xpZW50X25hbWUiOiJJU1cgQnVzaW5lc3MgUG9ydGFsIiwianRpIjoiMDI4MmJjNWEtYTEzOC00M2MxLTg2YTItMGE1ZjI2MDA5YmY4IiwiZW1haWwiOiJhZ2Jpc3dAc2hhcmtsYXNlcnMuY29tIiwicGFzc3BvcnRJZCI6IkVCMUMxMkFCLUU4NDAtNEM0Qy04MjgxLTdBNzc2MDZCQUUwMiJ9.ZCpIZjCDPGcJikOFgZ-0dJH3XiN1KMIV8WgeWi7PGPT1rz1znnlhexgrp3gpJdD32P4F8iaqYpDqHjMzJ3HsjgB81zmNcXxDiYUxKTWdar5xLt1YHIDzpag9A-WD3rYpjzHfsJWNrqAtbh7jS6o-H7lZUeUEGh7I22_pgEpVJ4G_cntrJ0dj4dLMsxwAM1AZRlqxGAXvfSRwCCmoRtoCI-w6RnV7qFDFGDKBkuX3gVV5iH1hnfe6i05tDaPMvVLYBpmWfitNhgjZHmQGVyA9_J84fqDCcVCN78xAskDlq65hMjJVJCOdXj-_mUTrXsffRbkLNh174klEosI_b4N-lQ";
}

export const axiosInstanceBatchCredit: AxiosInstanceType =
  getAxiosInstance(instanceBatchCredit);
